import activityHeaderStyles from '@github-ui/commenting/ActivityHeaderStyles.module.css'
import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {CommentAuthorAssociation} from '@github-ui/commenting/CommentAuthorAssociation'
import {SpammyBadge} from '@github-ui/commenting/SpammyBadge'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {HovercardAttributesResult} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {MarkdownLastEditedBy} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import {Label, Link, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import type {IssueBodyHeader$key} from './__generated__/IssueBodyHeader.graphql'
import type {IssueBodyHeaderSecondaryFragment$key} from './__generated__/IssueBodyHeaderSecondaryFragment.graphql'
import type {IssueBodyHeaderSecondaryFragment_issue$key} from './__generated__/IssueBodyHeaderSecondaryFragment_issue.graphql'
import headerStyles from './IssueBodyHeader.module.css'
import {IssueBodyHeaderActions, type IssueBodyHeaderActionsProps} from './IssueBodyHeaderActions'
import {IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

export type IssueBodyHeaderProps = {
  additionalActions?: React.ReactNode
  comment: IssueBodyHeader$key
  url?: string
  isPullRequest?: boolean
  actionProps?: IssueBodyHeaderActionsProps
  secondaryKey?: IssueBodyHeaderSecondaryFragment$key
  secondaryKeyIssue?: IssueBodyHeaderSecondaryFragment_issue$key
  authorHovercardAttrs?: HovercardAttributesResult
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueBodyHeaderSecondaryFragmentIssue = graphql`
  fragment IssueBodyHeaderSecondaryFragment_issue on Issue {
    repository {
      name
      isPrivate
      owner {
        login
      }
    }
    viaApp {
      name
      url
      logoUrl(size: 40)
      logoBackgroundColor
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueBodyHeaderSecondaryFragment = graphql`
  fragment IssueBodyHeaderSecondaryFragment on Comment {
    ...MarkdownEditHistoryViewer_comment
    ...MarkdownLastEditedBy
    showSpammyBadge
    authorAssociation
  }
`

export function IssueBodyHeader({
  additionalActions,
  comment,
  url,
  isPullRequest,
  actionProps,
  secondaryKey,
  secondaryKeyIssue,
  authorHovercardAttrs,
}: IssueBodyHeaderProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyHeader on Comment {
        ...IssueBodyHeaderActions_comment
        createdAt
        viewerDidAuthor
        author {
          ...IssueBodyHeaderAuthor
          ...IssueBodyHeaderActions
          avatarUrl
          login
          profileUrl
          __typename
        }
      }
    `,
    comment,
  )

  const createdAt = new Date(data.createdAt)
  const secondaryData = useFragment(IssueBodyHeaderSecondaryFragment, secondaryKey) ?? undefined
  const secondaryIssueData = useFragment(IssueBodyHeaderSecondaryFragmentIssue, secondaryKeyIssue) ?? undefined
  const isBot = data.author?.__typename === 'Bot'
  const isMannequin = data.author?.__typename === 'Mannequin'
  const viaAppData = secondaryIssueData?.viaApp
  const viaApp = isFeatureEnabled('issue_via_app_avatar') ? viaAppData : undefined

  const subText = (
    <>
      <span>{isPullRequest ? 'created ' : 'opened '}</span>
      {url ? (
        <Link href={url} data-testid="issue-body-header-link" className={headerStyles.dateLink}>
          <RelativeTime date={createdAt} className={headerStyles.RelativeTime}>
            on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
          </RelativeTime>
        </Link>
      ) : (
        <RelativeTime date={createdAt} className={headerStyles.RelativeTime}>
          on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
        </RelativeTime>
      )}
      {viaApp && (
        <span>
          {' '}
          – with{' '}
          <Link inline href={viaApp.url ?? undefined}>
            {viaApp.name}
          </Link>
        </span>
      )}
      {/** As Pull Request Issue Body Descriptions show Author label to the right of created at date, we need to render this additional label */}
      {isPullRequest && <Label className={headerStyles.authorLabel}>Author</Label>}
    </>
  )

  const {avatarUrl, login, profileUrl} = data.author ?? VALUES.ghost

  return (
    <div
      className={clsx(
        headerStyles.IssueBodyHeaderContainer,
        data.viewerDidAuthor ? headerStyles.viewerDidAuthor : headerStyles.viewerDidNotAuthor,
      )}
    >
      <div className={clsx(activityHeaderStyles.activityHeader, headerStyles.activityHeaderWrapper)}>
        <div
          className={clsx(
            styles.avatarInner,
            headerStyles.avatarContainer,
            isPullRequest && headerStyles.pullRequestAvatar,
          )}
        >
          <Link
            href={profileUrl ?? undefined}
            {...authorHovercardAttrs}
            aria-label={`@${login}'s profile`}
            className={profileUrl ? styles.avatarLink : styles.avatarWithoutLink}
          >
            <GitHubAvatar size={24} src={avatarUrl} alt={`@${login}`} />
          </Link>
          {viaApp && (
            <Link href={viaApp.url ?? undefined} className={headerStyles.viaAppAvatar} aria-label={viaApp.name}>
              <GitHubAvatar
                size={20}
                src={viaApp.logoUrl}
                alt={viaApp.name}
                square
                style={viaApp.logoBackgroundColor ? {backgroundColor: `#${viaApp.logoBackgroundColor}`} : undefined}
              />
            </Link>
          )}
        </div>

        <div className={clsx(activityHeaderStyles.narrowViewportWrapper, headerStyles.narrowViewportWrapper)}>
          <div className={headerStyles.titleSection}>
            <IssueBodyHeaderAuthor author={data.author || null} authorHovercardAttrs={authorHovercardAttrs} />
          </div>
          {(isBot || isMannequin) && <Label variant="secondary">{LABELS.authorLabel(isBot)}</Label>}
          <div className={clsx(activityHeaderStyles.footer, headerStyles.footerSection)}>{subText}</div>
        </div>

        <div className={clsx(activityHeaderStyles.narrowViewportWrapper, headerStyles.actionsWrapper)}>
          <div className={clsx(activityHeaderStyles.edits, headerStyles.editsSection)}>
            <MarkdownEditHistoryViewer
              editHistory={secondaryData}
              renderAnchor={() => <MarkdownLastEditedBy editInformation={secondaryData} />}
            />
          </div>
          <div className={headerStyles.badgesSection}>
            <div className={headerStyles.badgeGroup}>
              {secondaryData?.showSpammyBadge && <SpammyBadge />}
              {secondaryData && secondaryIssueData?.repository?.isPrivate === false && (
                <CommentAuthorAssociation
                  association={secondaryData.authorAssociation}
                  viewerDidAuthor={data.viewerDidAuthor}
                  org={secondaryIssueData.repository.owner?.login || ''}
                  repo={secondaryIssueData.repository.name}
                />
              )}
            </div>
            <div className={headerStyles.actionsSection}>
              {additionalActions}
              {actionProps && data.author && (
                <IssueBodyHeaderActions
                  url={actionProps.url}
                  comment={data}
                  issueBodyRef={actionProps.issueBodyRef}
                  onReplySelect={actionProps.onReplySelect}
                  startIssueBodyEdit={actionProps.startIssueBodyEdit}
                  onEditHover={actionProps.onEditHover}
                  viewerCanUpdate={actionProps.viewerCanUpdate}
                  viewerCanReport={actionProps.viewerCanReport}
                  viewerCanReportToMaintainer={actionProps.viewerCanReportToMaintainer}
                  viewerCanBlockFromOrg={actionProps.viewerCanBlockFromOrg}
                  viewerCanUnblockFromOrg={actionProps.viewerCanUnblockFromOrg}
                  issueId={actionProps.issueId}
                  owner={actionProps.owner}
                  ownerId={actionProps.ownerId}
                  ownerUrl={actionProps.ownerUrl}
                  author={data.author}
                  pendingBlock={actionProps.pendingBlock}
                  pendingUnblock={actionProps.pendingUnblock}
                  isDisabled={actionProps.isDisabled}
                  actionMenuPortalContainerName={actionProps.actionMenuPortalContainerName}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
